import type { FC } from 'react'
import { memo, Suspense, useCallback, useEffect, useRef } from 'react'
import { match, P } from 'ts-pattern'
import type {
  ArtworkDetailFragment,
  ArtworkWithTaskDetailBaseFragment,
  Manga,
  MangaType,
  RefType,
} from 'src/services/apollo/generated/graphql'
import { cx } from 'src/utils/cx'
import { useArtworkGridCols } from 'src/utils/hooks/useArtworkGridCols'
import { trackImageForRec, useTrackPageView } from 'src/utils/tracker'
import type { BaseArtworkItemPreviewCardProps } from '../Artwork/BaseArtworkItemPreviewCard'
import LoadingPage from '../LoadingPage'
import MangaDetail from '../Manga/Detail'
import { UIDialogResponsive } from '../ui/Modal'
import ArtworkDetail from './ArtworkDetail'

const ArtworkDetailDialog: FC<{
  artwork?: ArtworkDetailFragment
  artworkLoras?: ArtworkWithTaskDetailBaseFragment['loras']
  taskParameters?: ArtworkWithTaskDetailBaseFragment['taskParameters']

  /** Album is also a kind of manga */
  manga?: Manga
  open: boolean
  onClose: () => any
  /**
   * For `normal` tracking events
   * Inherited from artwork list
   */
  trackingArgs?: BaseArtworkItemPreviewCardProps['trackingArgs']
  /**
   * For `Rec` related tracking events
   * If not provided, won't enable trackImageForRec
   */
  extraLikeTrackingArgsForRec?: {
    artworkId: string
    index: number
    currentLikeCount: number
    type: RefType | MangaType
  }
}> = ({
  artwork,
  artworkLoras,
  taskParameters,

  manga,
  open,
  onClose,
  extraLikeTrackingArgsForRec,
  trackingArgs,
}) => {
  const totalCols = useArtworkGridCols()

  const onLikeClickForRec = useCallback(() => {
    if (!extraLikeTrackingArgsForRec) return
    trackImageForRec(
      'like_click',
      {
        feed: trackingArgs?.feed || 'trending',
        totalCols,
        scene: 'artwork_feed',
        likeFrom: 'detail',
        retrieverId: artwork?.feedMetadata?.retrieverId,
        ...extraLikeTrackingArgsForRec,
      },
      { feedUuid: trackingArgs?.feedUuid },
    )
  }, [
    extraLikeTrackingArgsForRec,
    artwork?.feedMetadata?.retrieverId,
    trackingArgs?.feed,
    trackingArgs?.feedUuid,
    totalCols,
  ])

  const trackPageView = useTrackPageView()
  const trackPageViewRef = useRef(trackPageView)
  trackPageViewRef.current = trackPageView

  useEffect(() => {
    if (open) {
      trackPageViewRef.current({ scene: 'artwork-detail', modal: true })
    }
  }, [open])

  return (
    <UIDialogResponsive
      open={open}
      onClose={onClose}
      className={cx(
        manga ? 'max-w-[600px]' : 'max-w-[1200px]',
        'mmd:w-[calc(100vw-64px)] [&>main]:max-mmd:overflow-y-auto',
      )}
      dismissable
    >
      <Suspense fallback={<LoadingPage />}>
        {match({ artwork, manga })
          .with({ manga: P.nonNullable }, ({ manga }) => (
            <MangaDetail manga={manga} onLikeClick={onLikeClickForRec} />
          ))
          .with({ artwork: P.nonNullable }, ({ artwork }) => (
            <ArtworkDetail
              artwork={artwork}
              artworkLoras={artworkLoras}
              taskParameters={taskParameters}
              onClose={onClose}
              onLikeClickForRec={onLikeClickForRec}
              trackingArgs={trackingArgs}
            />
          ))
          .otherwise(() => null)}
      </Suspense>
    </UIDialogResponsive>
  )
}

export default memo(ArtworkDetailDialog)
